import { Button } from '@/components/ui/Button'
import { Icon } from '@/components/ui/Icon'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { useRouteStore } from '@/store/route'
import { useSnackbarStore } from '@/store/snackbar'
import { StandardDonationStateAction } from '@types'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import styles from './CancelDonationButton.module.scss'

interface CancelFormValues {
  termination_note: string
}

const CANCEL_REASONS = [
  { value: 'donor_not_present', text: 'Donor not present' },
  { value: 'unable_to_accept', text: 'Unable to accept any donation items' },
  { value: 'confrontation', text: 'Confrontation/Misunderstanding with donor' },
  {
    value: 'truck_issues',
    text: 'Truck broke down/truck issues (cancel before starting donation)',
  },
  { value: 'other', text: 'Other' },
]

interface CancelDonationButtonProps {
  donationId: number
  onSuccess?: () => void
  className?: string
}

export const CancelDonationButton = ({
  donationId,
  onSuccess,
  className,
}: CancelDonationButtonProps) => {
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)
  const [isReasonSelectOpen, setIsReasonSelectOpen] = useState(false)
  const [cancelReason, setCancelReason] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const updateDonationState = useRouteStore(
    (state) => state.updateDonationState
  )
  const showSnackbar = useSnackbarStore((state) => state.show)

  const form = useForm<CancelFormValues>({
    defaultValues: { termination_note: '' },
  })
  const terminationNote = form.watch('termination_note')

  const isOtherReason = cancelReason === 'other'
  const canConfirm = cancelReason && (!isOtherReason || terminationNote.trim())

  const handleConfirm = async () => {
    if (canConfirm) {
      setIsLoading(true)
      try {
        await updateDonationState(donationId, {
          state_action: StandardDonationStateAction.CANCEL,
          termination_reason: cancelReason!,
          ...(isOtherReason && { termination_note: terminationNote.trim() }),
        })
        setIsCancelModalOpen(false)
        setCancelReason(null)
        form.reset()
        showSnackbar('Donation cancelled', 'success')
        onSuccess?.()
      } finally {
        setIsLoading(false)
      }
    }
  }

  const handleClose = () => {
    setIsCancelModalOpen(false)
    setCancelReason(null)
    form.reset()
  }

  return (
    <>
      <Button
        text="Cancel Donation"
        variant="error"
        onClick={() => setIsCancelModalOpen(true)}
        className={className}
        testId="cancel-donation-button"
      />

      <Modal
        isOpen={isCancelModalOpen}
        onClose={handleClose}
        title="Are you sure you want to cancel donation?"
        subtitle="The donation you are skipping will be next in line. For reordering the route, go to edit"
        buttonLayout="column"
        testId="cancel-donation-modal"
        buttons={[
          <Button
            key="confirm"
            text="Cancel Donation"
            variant="error"
            disabled={!canConfirm}
            onClick={handleConfirm}
            loading={isLoading}
            testId="cancel-donation-modal-confirm"
          />,
          <Button
            key="back"
            text="Go back"
            variant="default"
            onClick={handleClose}
            testId="cancel-donation-modal-back"
          />,
        ]}
      >
        <FormProvider {...form}>
          <div className={styles.container}>
            <p className={styles.label}>
              Choose reason for cancellation{' '}
              <span className={styles.required}>*</span>
            </p>
            <button
              type="button"
              className={styles.trigger}
              onClick={() => setIsReasonSelectOpen(true)}
              data-testid="cancel-reason-trigger"
            >
              {cancelReason ? (
                <span>
                  {CANCEL_REASONS.find((r) => r.value === cancelReason)?.text}
                </span>
              ) : (
                <span className={styles.placeholder}>Select a reason</span>
              )}
              <Icon name="chevron-down" size={16} color="#334155" />
            </button>
            {isOtherReason && (
              <Input<CancelFormValues>
                name="termination_note"
                label="Note"
                placeholder="Please provide details..."
                mode="textarea"
                required
              />
            )}
          </div>
        </FormProvider>
      </Modal>

      <Modal
        isOpen={isReasonSelectOpen}
        onClose={() => setIsReasonSelectOpen(false)}
        title="Select a reason"
        buttonLayout="column"
        nested
        testId="cancel-reason-modal"
        buttons={[
          <Button
            key="confirm"
            text="Confirm"
            variant="primary"
            onClick={() => setIsReasonSelectOpen(false)}
            testId="cancel-reason-modal-confirm"
          />,
          <Button
            key="back"
            text="Go back"
            variant="default"
            onClick={() => setIsReasonSelectOpen(false)}
            testId="cancel-reason-modal-back"
          />,
        ]}
      >
        <div className={styles.options} data-testid="cancel-reason-options">
          {CANCEL_REASONS.map((reason) => (
            <div
              key={reason.value}
              className={`${styles.option} ${cancelReason === reason.value ? styles.optionSelected : ''}`}
              onClick={() => setCancelReason(reason.value)}
              data-testid={`cancel-reason-option-${reason.value}`}
            >
              <span className={styles.optionText}>{reason.text}</span>
              <div className={styles.radio}>
                {cancelReason === reason.value && (
                  <div className={styles.radioInner} />
                )}
              </div>
            </div>
          ))}
        </div>
      </Modal>
    </>
  )
}
